/**
 * P4.2 (`docs/LOOP-ANFRAGEN-WER-IST-DRAN.md`) — die Aktion „Grund notieren" aus Entwurf 29a.
 *
 * Konzept § 4 (Lücke L3): „Der Entwurf sieht dafür die Aktion **„Grund notieren"** vor — also
 * ist der Grund eine **Eingabe des Betreibers**, keine Erfindung des Systems." Genau das ist
 * dieses Formular und mehr nicht: ein Freitextfeld, ein Knopf, keine Vorschläge, keine Auswahl
 * vorgefertigter Gründe.
 *
 * ── Warum kein Auswahlfeld mit „zu teuer / Termin belegt / anderer Anbieter" ─────────────────
 * Weil eine Liste die Antwort vorformt. Der Wert des Grundes liegt darin, dass er stimmt — E0.8
 * hat ihn aufgehoben, damit „zu teuer" von „Termin belegt" und „hat woanders gebucht"
 * unterscheidbar wird. Eine Auswahl macht aus dem, was war, das Nächstliegende aus vier
 * Kacheln. Freitext ist hier die genauere Messung, nicht die bequemere.
 *
 * ── Diese Datei formuliert nichts über den Vorgang ───────────────────────────────────────────
 * Sie zeigt den Satz, den `leiteStandAb` geliefert hat (`stand.satz`), damit sichtbar ist, worauf
 * sich der Grund bezieht — sie baut ihn nicht. Nach dem Speichern nennt derselbe Satz den Grund,
 * weil die Regel ihn dann als Baustein führt (`@maestro/anfrage-stand`, Zeile 12 aus § 6). Die
 * Oberfläche schreibt dafür keine Zeile Text.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Button, Drawer, Zeichenzaehler, Textarea } from "./components";

export interface GrundNotierenProps {
  open: boolean;
  /** Name der Kundin/des Kunden — nur zur Orientierung im Kopf, nie Teil des gespeicherten Grundes. */
  kundenName: string;
  /** Der Stand-Satz der Zeile, wie die Regel ihn liefert. */
  satz: string | null;
  /** Läuft gerade ein Speichern? Der Aufrufer besitzt den Zustand, damit die Liste ihn kennt. */
  busy?: boolean;
  /** Fehlermeldung des Servers, unverfälscht durchgereicht. */
  fehler?: string | null;
  onClose: () => void;
  onSpeichern: (grund: string) => void;
}

export function GrundNotierenDialog({
  open,
  kundenName,
  satz,
  busy,
  fehler,
  onClose,
  onSpeichern,
}: GrundNotierenProps) {
  const [grund, setGrund] = useState("");

  // Beim Öffnen leer anfangen: der Grund gehört zu DIESER Zeile, nicht zur vorigen.
  useEffect(() => {
    if (open) setGrund("");
  }, [open]);

  function absenden(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const wert = grund.trim();
    // Ein leerer Grund ist kein Grund — dieselbe Regel wie serverseitig (routes/events.ts).
    if (wert === "") return;
    onSpeichern(wert);
  }

  return (
    <Drawer open={open} onClose={onClose} title="Grund notieren" subtitle={kundenName}>
      <form onSubmit={absenden} className="flex flex-col gap-4">
        {satz && (
          <p className="font-body-sm text-[12.5px] leading-relaxed text-on-surface-variant dark:text-secondary-fixed-dim">
            {satz}
          </p>
        )}
        <label className="flex flex-col gap-1.5">
          <span className="font-body-sm text-[12px] font-medium text-on-surface-variant">
            Warum ist daraus nichts geworden?
          </span>
          <Textarea
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
            rows={4}
            maxLength={1000}
            autoFocus
            placeholder="z. B. zu teuer, Termin war belegt, hat woanders gebucht"
            className="resize-y"
          />
          <Zeichenzaehler current={grund.length} max={1000} />
        </label>
        <p className="font-body-sm text-[11.5px] leading-relaxed text-on-surface-variant/80">
          Steht der Grund, nennt ihn der Stand-Satz in der Liste. Ohne Grund bleibt der Satz, wie er
          ist — es wird nichts vermutet.
        </p>
        {fehler && (
          <p role="alert" className="font-body-sm text-[12px] text-error">
            {fehler}
          </p>
        )}
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={busy || grund.trim() === ""}>
            {busy ? "Wird gespeichert …" : "Grund speichern"}
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
